/**
 * The productions list (PLAN §5.1, D25's "productions list with as-run"):
 * each production's studio, what it runs, rehearsal, status and when it was
 * made; open one (the playout view), end one. `productions`, `loading` and
 * `error` are independent props (a refetch keeps the rows on screen).
 * Creating and ending are offered only to who may (`canCreate`, `endable`).
 */
import type { ProductionStatusOpenWire } from '@acp/api-contract/productions.js';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { isPlainClick } from '../../lib/links.js';
import { UNKNOWN_HINT, UNKNOWN_LABEL } from '../../lib/openEnum.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type ProductionRow = {
  readonly productionId: string;
  readonly studioName: string;
  /** The show style it runs (D32); null for a production made before show styles. */
  readonly showStyleName: string | null;
  /** `Rundown: Evening news` / `Playlist: Friday`. */
  readonly source: string;
  readonly rehearsal: boolean;
  /** It runs in AWS Fargate, a node of the cloud site (PLAN §9). */
  readonly cloud: boolean;
  readonly status: ProductionStatusOpenWire;
  /** Created, as the browser shows a date and time. */
  readonly created: string;
  readonly createdBy: string;
  /** The user may end it (and it is not ended yet). */
  readonly endable: boolean;
};

export type ProductionListProps = {
  readonly productions: readonly ProductionRow[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly canCreate: boolean;
  readonly onCreate: () => void;
  readonly onRetry: () => void;
  readonly onOpen: (productionId: string) => void;
  readonly onEnd: (productionId: string) => void;
};

export function productionHref(productionId: string): string {
  return `/productions/${encodeURIComponent(productionId)}`;
}

function StatusBadge({ status }: { readonly status: ProductionStatusOpenWire }) {
  if (status === 'active') return <Badge variant="onair">Active</Badge>;
  if (status === 'ended') return <Badge variant="offline">Ended</Badge>;
  if (status === 'created') return <Badge variant="standby">Created</Badge>;
  // A status a newer server reports that this build does not know (an open enum, D37).
  return (
    <Badge variant="secondary" title={UNKNOWN_HINT}>
      {UNKNOWN_LABEL}
    </Badge>
  );
}

export function ProductionList({ productions, loading, error, canCreate, onCreate, onRetry, onOpen, onEnd }: ProductionListProps) {
  return (
    <Card data-testid="production-list">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Productions</CardTitle>
          <CardDescription>Runs of a rundown or a playlist in a studio, newest first.</CardDescription>
        </div>
        {canCreate ? (
          <Button onClick={onCreate} data-testid="create-production">
            <PlusIcon />
            New production
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load productions: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && productions.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading productions…</p> : null}
        {!loading && error === null && productions.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="productions-empty">
            No productions yet.{canCreate ? ' Create one to go to air.' : ''}
          </p>
        ) : null}
        {productions.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Runs</TableHead>
                <TableHead>Studio</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {productions.map((p) => (
                <TableRow key={p.productionId} data-testid="production-row" data-status={p.status}>
                  <TableCell className="font-medium">
                    <a
                      href={productionHref(p.productionId)}
                      className="hover:underline"
                      data-testid="open-production"
                      onClick={(event) => {
                        if (!isPlainClick(event)) return;
                        event.preventDefault();
                        onOpen(p.productionId);
                      }}
                    >
                      {p.source}
                    </a>
                    <div className="font-mono text-xs text-muted-foreground">{p.productionId}</div>
                  </TableCell>
                  <TableCell>
                    {p.studioName}
                    {p.showStyleName === null ? null : (
                      <div className="text-xs text-muted-foreground" data-testid="production-show-style">
                        {p.showStyleName}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="flex gap-1">
                      <StatusBadge status={p.status} />
                      {p.rehearsal ? <Badge variant="preview">Rehearsal</Badge> : null}
                      {p.cloud ? (
                        <Badge variant="outline" data-testid="cloud-badge">
                          Cloud
                        </Badge>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.created} · {p.createdBy}
                  </TableCell>
                  <TableCell>
                    {p.endable ? (
                      <Button variant="outline" size="sm" onClick={() => onEnd(p.productionId)} data-testid="end-production">
                        End
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
